import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import config from '../../../config.js';
import {
  ArtifactDownloadResponse,
  CreateProjectRequest,
  ProjectArtifact,
  ProjectArtifactKind,
  ProjectDetail,
  ProjectSummary,
} from './models/project.models';

@Injectable({
  providedIn: 'root',
})
export class ProjectApiService {
  private apiUrl = `${config.API_GATEWAY_ENDPOINT}/projects`;

  constructor(private http: HttpClient) {}

  createProject(body: CreateProjectRequest): Observable<ProjectDetail> {
    return this.http.post<ProjectDetail>(this.apiUrl, body);
  }

  listProjects(): Observable<ProjectSummary[]> {
    return this.http
      .get<{ items: ProjectSummary[] }>(this.apiUrl)
      .pipe(map((response) => response.items));
  }

  getProject(projectId: string): Observable<ProjectDetail> {
    return this.http.get<ProjectDetail>(`${this.apiUrl}/${projectId}`);
  }

  /**
   * Records how far the requester has reached.
   *
   * PUT, not PATCH: the API Gateway resource declares only GET, POST, PUT and
   * DELETE, so a PATCH would work against a local server and 403 once deployed.
   */
  updateStageCursor(
    projectId: string,
    currentStage: number,
    maxUnlockedStage: number
  ): Observable<ProjectDetail> {
    return this.http.put<ProjectDetail>(`${this.apiUrl}/${projectId}/stage`, {
      current_stage: currentStage,
      max_unlocked_stage: maxUnlockedStage,
    });
  }

  getArtifact(projectId: string, kind: ProjectArtifactKind): Observable<ProjectArtifact> {
    return this.http.get<ProjectArtifact>(`${this.apiUrl}/${projectId}/artifacts/${kind}`);
  }

  listArtifactVersions(projectId: string, kind: ProjectArtifactKind): Observable<ProjectArtifact[]> {
    return this.http
      .get<{ items: ProjectArtifact[] }>(`${this.apiUrl}/${projectId}/artifacts/${kind}/versions`)
      .pipe(map((response) => response.items));
  }

  /**
   * A time-limited download link for the current version of an artifact.
   *
   * This is what makes a restored project's files re-downloadable — the job
   * that originally produced them may be long gone, but the artifact it
   * persisted is still on the project.
   */
  downloadArtifact(projectId: string, kind: ProjectArtifactKind): Observable<ArtifactDownloadResponse> {
    return this.http.get<ArtifactDownloadResponse>(
      `${this.apiUrl}/${projectId}/artifacts/${kind}/download`
    );
  }
}
